<script setup>
import { ref, onMounted, watch, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import SettingsLayout from '../SettingsLayout.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import LocationMapModal from './LocationMapModal.vue';
import {
  storeLocationsApi,
  maintenanceZonesApi,
} from 'dashboard/api/locations';
import TabBar from 'dashboard/components-next/tabbar/TabBar.vue';

const confirmDialog = ref(null);
const isLoading = ref(false);
const isImporting = ref(false);
const csvUpload = ref(null);
const showModal = ref(false);
const activeTab = ref(0); // 0 = Lojas, 1 = Zonas
const { t, locale } = useI18n();

const tabs = computed(() => [
  { key: 0, name: t('LOCATIONS.TABS.STORES') },
  { key: 1, name: t('LOCATIONS.TABS.UNAVAILABLE_AREAS') },
  { key: 2, name: t('LOCATIONS.TABS.API') },
]);
const tabsForTabBar = computed(() =>
  tabs.value.map(tab => ({ label: tab.name, key: tab.key }))
);

const storeLocations = ref([]);
const maintenanceZones = ref([]);

// Pagination Meta
const metaStores = ref({ current_page: 1, total_pages: 1, count: 0 });
const metaZones = ref({ current_page: 1, total_pages: 1, count: 0 });

const accountId = computed(() => {
  const pathParts = window.location.pathname.split('/');
  const accountIndex = pathParts.indexOf('accounts');
  return accountIndex !== -1 ? pathParts[accountIndex + 1] : '1';
});
const apiBaseUrl = computed(
  () => `${window.location.origin}/api/v1/accounts/${accountId.value}`
);

const currentModalType = ref('store');
const currentModalMode = ref('create');
const currentLocationData = ref({});

const fetchLocations = async (page = 1) => {
  try {
    isLoading.value = true;
    const { data } = await storeLocationsApi.get({ page });
    storeLocations.value = data.payload;
    metaStores.value = data.meta;
  } catch (error) {
    useAlert(t('LOCATIONS.ERRORS.LOAD_STORES'));
  } finally {
    isLoading.value = false;
  }
};

const fetchZones = async (page = 1) => {
  try {
    isLoading.value = true;
    const { data } = await maintenanceZonesApi.get({ page });
    maintenanceZones.value = data.payload;
    metaZones.value = data.meta;
  } catch (error) {
    useAlert(t('LOCATIONS.ERRORS.LOAD_ZONES'));
  } finally {
    isLoading.value = false;
  }
};

const initData = () => {
  fetchLocations();
  fetchZones();
};

onMounted(() => {
  initData();
});

watch(activeTab, () => {
  if (activeTab.value === 0 && storeLocations.value.length === 0)
    fetchLocations();
  if (activeTab.value === 1 && maintenanceZones.value.length === 0)
    fetchZones();
});

const openCreateModal = () => {
  currentModalType.value = activeTab.value === 0 ? 'store' : 'zone';
  currentModalMode.value = 'create';
  currentLocationData.value = {};
  showModal.value = true;
};

const editLocation = item => {
  currentModalType.value = activeTab.value === 0 ? 'store' : 'zone';
  currentModalMode.value = 'edit';
  currentLocationData.value = { ...item };
  showModal.value = true;
};

const deleteLocation = async item => {
  const ok = await confirmDialog.value.showConfirmation();
  if (!ok) return;

  try {
    if (activeTab.value === 0) {
      await storeLocationsApi.delete(item.id);
      await fetchLocations(metaStores.value.current_page);
    } else {
      await maintenanceZonesApi.delete(item.id);
      await fetchZones(metaZones.value.current_page);
    }
    useAlert(t('LOCATIONS.SUCCESS.DELETED'));
  } catch (error) {
    useAlert(error.response?.data?.error || t('LOCATIONS.ERRORS.DELETE'));
  }
};

const handleModalSubmit = async formData => {
  try {
    isLoading.value = true;
    const isStore = currentModalType.value === 'store';
    const api = isStore ? storeLocationsApi : maintenanceZonesApi;
    const payloadKey = isStore ? 'store_location' : 'maintenance_zone';
    const payload = { [payloadKey]: formData };

    if (currentModalMode.value === 'create') {
      await api.create(payload);
      useAlert(t('LOCATIONS.SUCCESS.CREATED'));
    } else {
      await api.update(formData.id, payload);
      useAlert(t('LOCATIONS.SUCCESS.UPDATED'));
    }

    showModal.value = false;
    if (isStore) fetchLocations(metaStores.value.current_page);
    else fetchZones(metaZones.value.current_page);
  } catch (error) {
    useAlert(error.response?.data?.error || t('LOCATIONS.ERRORS.SAVE'));
  } finally {
    isLoading.value = false;
  }
};

const handleFileUpload = async event => {
  const file = event.target.files[0];
  if (!file) return;

  try {
    isImporting.value = true;
    if (activeTab.value === 0) {
      await storeLocationsApi.importCSV(file);
    } else {
      await maintenanceZonesApi.importCSV(file);
    }
    if (activeTab.value === 0) await fetchLocations(1);
    else await fetchZones(1);
    useAlert(t('LOCATIONS.SUCCESS.IMPORTED'));
  } catch (error) {
    useAlert(error.response?.data?.error || t('LOCATIONS.ERRORS.IMPORT'));
  } finally {
    isImporting.value = false;
    event.target.value = '';
  }
};

const downloadTemplate = () => {
  const csvEscape = value => `"${String(value).replace(/"/g, '""')}"`;
  let csvContent;
  let filename;

  if (activeTab.value === 0) {
    const headers =
      locale.value === 'en'
        ? 'name,address,phone,latitude,longitude,zip_code,max_radius_km,pricing_model,base_fee,fee_per_km,active'
        : 'nome,endereco,telefone,latitude,longitude,cep,raio_km,modelo_cobranca,taxa_base,taxa_por_km,ativo';
    const sampleRow = [
      t('LOCATIONS.CSV_SAMPLE_STORE'),
      t('LOCATIONS.CSV_SAMPLE_STORE_ADDRESS'),
      '31999999999',
      '-19.956327',
      '-43.983936',
      '30516-210',
      '15',
      'fixed',
      '10',
      '0',
      'true',
    ];
    csvContent = [headers, sampleRow.map(csvEscape).join(',')].join('\n');
    filename = 'modelo_importacao_lojas.csv';
  } else {
    const headers =
      locale.value === 'en'
        ? 'name,store_location_id,latitude,longitude,radius_km,message,active'
        : 'nome,loja_id,latitude,longitude,raio_km,mensagem,ativo';
    const sampleRow = [
      t('LOCATIONS.CSV_SAMPLE_ZONE'),
      '',
      '-19.956327',
      '-43.983936',
      '5.0',
      t('LOCATIONS.CSV_SAMPLE_ZONE_MESSAGE'),
      'true',
    ];
    csvContent = [headers, sampleRow.map(csvEscape).join(',')].join('\n');
    filename = 'modelo_importacao_areas.csv';
  }

  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

const nextStorePage = () => {
  if (metaStores.value.current_page < metaStores.value.total_pages) {
    fetchLocations(metaStores.value.current_page + 1);
  }
};

const prevStorePage = () => {
  if (metaStores.value.current_page > 1) {
    fetchLocations(metaStores.value.current_page - 1);
  }
};

const nextZonePage = () => {
  if (metaZones.value.current_page < metaZones.value.total_pages) {
    fetchZones(metaZones.value.current_page + 1);
  }
};

const prevZonePage = () => {
  if (metaZones.value.current_page > 1) {
    fetchZones(metaZones.value.current_page - 1);
  }
};
const copyToClipboard = text => {
  navigator.clipboard.writeText(text);
  useAlert(t('LOCATIONS.SUCCESS.COPIED'));
};
</script>

<template>
  <SettingsLayout :is-loading="isLoading">
    <template #header>
      <BaseSettingsHeader
        :title="$t('LOCATIONS.HEADER')"
        :description="t('LOCATIONS.DESCRIPTION')"
      >
        <template #tabs>
          <TabBar
            :tabs="tabsForTabBar"
            :initial-active-tab="activeTab"
            @tab-changed="tab => (activeTab = tab.key)"
          />
        </template>
        <template #actions>
          <div class="flex items-center gap-2">
            <template v-if="activeTab === 0 || activeTab === 1">
              <input
                ref="csvUpload"
                type="file"
                accept=".csv"
                class="hidden"
                @change="handleFileUpload"
              />
              <NextButton
                faded
                slate
                icon="i-lucide-upload"
                :label="t('LOCATIONS.IMPORT_CSV')"
                :is-loading="isImporting"
                @click="csvUpload?.click()"
              />
              <NextButton
                faded
                slate
                icon="i-lucide-download"
                :label="t('LOCATIONS.DOWNLOAD_TEMPLATE')"
                @click="downloadTemplate"
              />
            </template>
            <NextButton
              v-if="activeTab === 0 || activeTab === 1"
              icon="i-lucide-plus"
              :label="
                activeTab === 0
                  ? t('LOCATIONS.ADD_STORE')
                  : t('LOCATIONS.ADD_ZONE')
              "
              @click="openCreateModal"
            />
          </div>
        </template>
      </BaseSettingsHeader>
    </template>

    <div class="p-6 flex flex-col items-center">
      <div class="w-full max-w-7xl mx-auto space-y-6">
        <!-- Store Locations Table -->
        <div v-if="activeTab === 0">
          <table class="w-full text-left border-collapse table-fixed">
            <thead>
              <tr class="border-b border-slate-200 text-slate-600">
                <th class="py-2 w-16">{{ $t('LOCATIONS.ID') }}</th>
                <th class="py-2 w-3/12">{{ $t('LOCATIONS.NAME') }}</th>
                <th class="py-2 w-5/12">
                  {{ $t('LOCATIONS.ADDRESS_PHONE') }}
                </th>
                <th class="py-2 w-24">{{ $t('LOCATIONS.STATUS') }}</th>
                <th class="py-2 w-28 text-right">
                  {{ $t('LOCATIONS.ACTIONS') }}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="item in storeLocations"
                :key="item.id"
                class="border-b border-slate-100"
              >
                <td class="py-3 font-mono text-xs text-slate-500">
                  {{ $t('COMMON.HASH_PREFIX') }}{{ item.id }}
                </td>
                <td class="py-3 font-medium truncate" :title="item.name">
                  {{ item.name }}
                </td>
                <td class="py-3 text-slate-600">
                  <div class="text-sm truncate" :title="item.address">
                    {{ item.address || '-' }}
                  </div>
                  <div v-if="item.phone" class="text-xs text-slate-400 mt-1">
                    {{ $t('LOCATIONS.PHONE', { phone: item.phone }) }}
                  </div>
                </td>
                <td class="py-3">
                  <span
                    v-if="item.active"
                    class="px-2 py-1 bg-green-100 text-green-800 rounded-md text-xs"
                    >{{ $t('LOCATIONS.ACTIVE') }}</span
                  >
                  <span
                    v-else
                    class="px-2 py-1 bg-red-100 text-red-800 rounded-md text-xs"
                    >{{ $t('LOCATIONS.INACTIVE') }}</span
                  >
                </td>
                <td class="py-3 text-right">
                  <NextButton
                    size="sm"
                    faded
                    slate
                    icon="i-lucide-pencil"
                    class="mr-2"
                    @click="editLocation(item)"
                  />
                  <NextButton
                    size="sm"
                    faded
                    color="ruby"
                    icon="i-lucide-trash"
                    @click="deleteLocation(item)"
                  />
                </td>
              </tr>
              <tr v-if="!storeLocations.length && !isLoading">
                <td colspan="4" class="py-6 text-center text-slate-500">
                  {{ $t('LOCATIONS.NO_STORES') }}
                </td>
              </tr>
            </tbody>
          </table>

          <!-- Pagination Stores -->
          <div
            v-if="metaStores.total_pages > 1"
            class="flex justify-between items-center mt-4"
          >
            <span class="text-sm text-slate-500">{{
              $t('LOCATIONS.TOTAL', { count: metaStores.count })
            }}</span>
            <div class="flex gap-2 items-center">
              <NextButton
                size="sm"
                faded
                slate
                icon="i-lucide-chevron-left"
                :disabled="metaStores.current_page === 1"
                @click="prevStorePage"
              />
              <span class="mx-4 text-sm text-slate-600 dark:text-slate-400">{{
                $t('LOCATIONS.PAGE', {
                  current: metaStores.current_page,
                  total: metaStores.total_pages,
                })
              }}</span>
              <NextButton
                size="sm"
                faded
                slate
                icon="i-lucide-chevron-right"
                :disabled="metaStores.current_page === metaStores.total_pages"
                @click="nextStorePage"
              />
            </div>
          </div>
        </div>

        <!-- Maintenance Zones Table -->
        <div v-if="activeTab === 1">
          <table class="w-full text-left border-collapse table-fixed">
            <thead>
              <tr class="border-b border-slate-200 text-slate-600">
                <th class="py-2 w-16">{{ $t('LOCATIONS.ID') }}</th>
                <th class="py-2 w-3/12">{{ $t('LOCATIONS.NAME') }}</th>
                <th class="py-2 w-3/12">
                  {{ $t('LOCATIONS.LINKED_STORE') }}
                </th>
                <th class="py-2 w-2/12">
                  {{ $t('LOCATIONS.RADIUS') }}
                </th>
                <th class="py-2 w-24">{{ $t('LOCATIONS.STATUS') }}</th>
                <th class="py-2 w-28 text-right">
                  {{ $t('LOCATIONS.ACTIONS') }}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="item in maintenanceZones"
                :key="item.id"
                class="border-b border-slate-100"
              >
                <td class="py-3 font-mono text-xs text-slate-500">
                  {{ $t('COMMON.HASH_PREFIX') }}{{ item.id }}
                </td>
                <td class="py-3 font-medium truncate" :title="item.name">
                  {{ item.name }}
                </td>
                <td class="py-3 text-slate-600 truncate">
                  {{ item.store_location_name || $t('LOCATIONS.GLOBAL_AREA') }}
                </td>
                <td class="py-3 text-slate-600 truncate">
                  {{
                    $t('LOCATIONS.RADIUS_VALUE', {
                      radius: item.radius_km,
                    })
                  }}
                </td>
                <td class="py-3">
                  <span
                    v-if="item.active"
                    class="px-2 py-1 bg-green-100 text-green-800 rounded-md text-xs"
                    >{{ $t('LOCATIONS.ACTIVE') }}</span
                  >
                  <span
                    v-else
                    class="px-2 py-1 bg-red-100 text-red-800 rounded-md text-xs"
                    >{{ $t('LOCATIONS.INACTIVE') }}</span
                  >
                </td>
                <td class="py-3 text-right">
                  <NextButton
                    size="sm"
                    faded
                    slate
                    icon="i-lucide-pencil"
                    class="mr-2"
                    @click="editLocation(item)"
                  />
                  <NextButton
                    size="sm"
                    faded
                    color="ruby"
                    icon="i-lucide-trash"
                    @click="deleteLocation(item)"
                  />
                </td>
              </tr>
              <tr v-if="!maintenanceZones.length && !isLoading">
                <td colspan="6" class="py-6 text-center text-slate-500">
                  {{ $t('LOCATIONS.NO_ZONES') }}
                </td>
              </tr>
            </tbody>
          </table>

          <!-- Pagination Zones -->
          <div
            v-if="metaZones.total_pages > 1"
            class="flex justify-between items-center mt-4"
          >
            <span class="text-sm text-slate-500">{{
              $t('LOCATIONS.TOTAL_ZONES', { count: metaZones.count })
            }}</span>
            <div class="flex gap-2 items-center">
              <NextButton
                size="sm"
                faded
                slate
                icon="i-lucide-chevron-left"
                :disabled="metaZones.current_page === 1"
                @click="prevZonePage"
              />
              <span class="mx-4 text-sm text-slate-600 dark:text-slate-400"
                >{{
                  $t('LOCATIONS.PAGE', {
                    current: metaZones.current_page,
                    total: metaZones.total_pages,
                  })
                }}
                {{ metaZones.total_pages }}</span
              >
              <NextButton
                size="sm"
                faded
                slate
                icon="i-lucide-chevron-right"
                :disabled="metaZones.current_page === metaZones.total_pages"
                @click="nextZonePage"
              />
            </div>
          </div>
        </div>

        <!-- API Integration Tab -->
        <div v-if="activeTab === 2" class="space-y-6">
          <!-- Store Locations API -->
          <div
            class="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 overflow-hidden"
          >
            <div
              class="px-6 py-4 border-b border-slate-100 dark:border-slate-700/50 flex justify-between items-center"
            >
              <div class="flex items-center gap-3">
                <div
                  class="w-8 h-8 rounded bg-blue-100 dark:bg-blue-900/40 flex items-center justify-center"
                >
                  <span
                    class="icon i-lucide-store text-blue-600 dark:text-blue-400"
                  />
                </div>
                <div>
                  <h3 class="font-medium text-slate-800 dark:text-slate-200">
                    {{ $t('LOCATIONS.STORES_API_TITLE') }}
                  </h3>
                  <p class="text-xs text-slate-500 dark:text-slate-400">
                    {{ $t('LOCATIONS.STORES_API_DESCRIPTION') }}
                  </p>
                </div>
              </div>
            </div>
            <div class="p-6 space-y-6">
              <div class="flex items-center gap-3">
                <input
                  type="text"
                  readonly
                  :value="`${apiBaseUrl}/store_locations`"
                  class="flex-1 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 text-sm rounded-lg px-4 py-2.5 focus:outline-none"
                />
                <button
                  class="px-4 py-2.5 bg-blue-50 hover:bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:hover:bg-blue-900/50 dark:text-blue-400 rounded-lg text-sm font-medium transition-colors flex items-center gap-2"
                  @click="copyToClipboard(`${apiBaseUrl}/store_locations`)"
                >
                  <span class="icon i-lucide-copy" />
                  {{ $t('LOCATIONS.COPY_URL') }}
                </button>
              </div>

              <!-- API examples contain literal HTTP/JSON syntax and are intentionally not translated. -->
              <!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text, vue/html-closing-bracket-newline -->
              <div
                class="bg-slate-900 rounded-lg p-4 font-mono text-xs text-slate-300 overflow-x-auto space-y-4"
              >
                <div>
                  <span class="text-emerald-400">POST</span>
                  {{ apiBaseUrl }}/store_locations
                  <span class="text-slate-500 ml-2">{{
                    $t('LOCATIONS.CREATE_STORE')
                  }}</span>
                </div>
                <div>
                  <span class="text-yellow-400">PUT</span>
                  {{ apiBaseUrl }}/store_locations/<span class="text-white"
                    >:id</span
                  >
                  <span class="text-slate-500 ml-2">{{
                    $t('LOCATIONS.UPDATE_STORE')
                  }}</span>
                </div>
                <div>
                  <span class="text-red-400">DELETE</span>
                  {{ apiBaseUrl }}/store_locations/<span class="text-white"
                    >:id</span
                  >
                  <span class="text-slate-500 ml-2">{{
                    $t('LOCATIONS.DELETE_STORE')
                  }}</span>
                </div>

                <div class="pt-4 border-t border-slate-700">
                  <span class="text-yellow-400">{{
                    $t('LOCATIONS.API_HEADERS')
                  }}</span
                  ><br />
                  api_access_token:
                  <span class="text-slate-400">{{
                    $t('LOCATIONS.API_TOKEN_HELP')
                  }}</span>
                </div>

                <div class="pt-4">
                  <span class="text-blue-400"
                    >{{ $t('LOCATIONS.API_BODY_EXAMPLE') }}:</span
                  ><br />
                  { "store_location": {<br />
                  &nbsp;&nbsp;"name": "{{
                    $t('LOCATIONS.API_SAMPLE_STORE')
                  }}",<br />
                  &nbsp;&nbsp;"address": "{{
                    $t('LOCATIONS.API_SAMPLE_ADDRESS')
                  }}",<br />
                  &nbsp;&nbsp;"phone": "553199999999",<br />
                  &nbsp;&nbsp;"latitude": -19.956327,<br />
                  &nbsp;&nbsp;"longitude": -43.983936,<br />
                  &nbsp;&nbsp;"max_radius_km": 5.0,<br />
                  &nbsp;&nbsp;"active": true<br />
                  } }
                </div>
              </div>
              <!-- eslint-enable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text, vue/html-closing-bracket-newline -->
            </div>
          </div>

          <!-- Maintenance Zones API -->
          <div
            class="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 overflow-hidden"
          >
            <div
              class="px-6 py-4 border-b border-slate-100 dark:border-slate-700/50 flex justify-between items-center"
            >
              <div class="flex items-center gap-3">
                <div
                  class="w-8 h-8 rounded bg-red-100 dark:bg-red-900/40 flex items-center justify-center"
                >
                  <span
                    class="icon i-lucide-map-pin-off text-red-600 dark:text-red-400"
                  />
                </div>
                <div>
                  <h3 class="font-medium text-slate-800 dark:text-slate-200">
                    {{ $t('LOCATIONS.ZONES_API_TITLE') }}
                  </h3>
                  <p class="text-xs text-slate-500 dark:text-slate-400">
                    {{ $t('LOCATIONS.ZONES_API_DESCRIPTION') }}
                  </p>
                </div>
              </div>
            </div>
            <div class="p-6 space-y-6">
              <div class="flex items-center gap-3">
                <input
                  type="text"
                  readonly
                  :value="`${apiBaseUrl}/maintenance_zones`"
                  class="flex-1 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 text-sm rounded-lg px-4 py-2.5 focus:outline-none"
                />
                <button
                  class="px-4 py-2.5 bg-blue-50 hover:bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:hover:bg-blue-900/50 dark:text-blue-400 rounded-lg text-sm font-medium transition-colors flex items-center gap-2"
                  @click="copyToClipboard(`${apiBaseUrl}/maintenance_zones`)"
                >
                  <span class="icon i-lucide-copy" />
                  {{ $t('LOCATIONS.COPY_URL') }}
                </button>
              </div>

              <!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text, vue/html-closing-bracket-newline -->
              <div
                class="bg-slate-900 rounded-lg p-4 font-mono text-xs text-slate-300 overflow-x-auto space-y-4"
              >
                <div>
                  <span class="text-emerald-400">POST</span>
                  {{ apiBaseUrl }}/maintenance_zones
                  <span class="text-slate-500 ml-2">{{
                    $t('LOCATIONS.CREATE_ZONE')
                  }}</span>
                </div>
                <div>
                  <span class="text-yellow-400">PUT</span>
                  {{ apiBaseUrl }}/maintenance_zones/<span class="text-white"
                    >:id</span
                  >
                  <span class="text-slate-500 ml-2">{{
                    $t('LOCATIONS.UPDATE_ZONE')
                  }}</span>
                </div>
                <div>
                  <span class="text-red-400">DELETE</span>
                  {{ apiBaseUrl }}/maintenance_zones/<span class="text-white"
                    >:id</span
                  >
                  <span class="text-slate-500 ml-2">{{
                    $t('LOCATIONS.DELETE_ZONE')
                  }}</span>
                </div>

                <div class="pt-4 border-t border-slate-700">
                  <span class="text-yellow-400">{{
                    $t('LOCATIONS.API_HEADERS')
                  }}</span
                  ><br />
                  api_access_token:
                  <span class="text-slate-400">{{
                    $t('LOCATIONS.API_TOKEN_HELP')
                  }}</span>
                </div>

                <div class="pt-4">
                  <span class="text-blue-400"
                    >{{ $t('LOCATIONS.API_BODY_EXAMPLE') }}:</span
                  ><br />
                  { "maintenance_zone": {<br />
                  &nbsp;&nbsp;"name": "{{
                    $t('LOCATIONS.API_SAMPLE_ZONE')
                  }}",<br />
                  &nbsp;&nbsp;"store_location_id": 1,
                  <span class="text-slate-500">{{
                    $t('LOCATIONS.API_SAMPLE_STORE_ID_HELP')
                  }}</span
                  ><br />
                  &nbsp;&nbsp;"latitude": -19.956327,<br />
                  &nbsp;&nbsp;"longitude": -43.983936,<br />
                  &nbsp;&nbsp;"radius_km": 2.5,<br />
                  &nbsp;&nbsp;"shape_type": "circle",
                  <span class="text-slate-500">{{
                    $t('LOCATIONS.API_SHAPE_VALUES')
                  }}</span
                  ><br />
                  &nbsp;&nbsp;"active": true<br />
                  } }
                </div>
              </div>
              <!-- eslint-enable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text, vue/html-closing-bracket-newline -->
            </div>
          </div>
        </div>
      </div>
    </div>

    <LocationMapModal
      v-if="showModal"
      :show="showModal"
      :type="currentModalType"
      :mode="currentModalMode"
      :location-data="currentLocationData"
      :stores="storeLocations"
      @close="showModal = false"
      @submit="handleModalSubmit"
    />

    <woot-confirm-modal
      ref="confirmDialog"
      :title="$t('LOCATIONS.DELETE')"
      :description="$t('LOCATIONS.DELETE_CONFIRMATION')"
      :confirm-label="$t('LOCATIONS.DELETE')"
      :cancel-label="$t('LOCATIONS.CANCEL')"
    />
  </SettingsLayout>
</template>
